import "@/components/Interface/FormFields/FormField/FormField.scss";
import "./DateInput.scss";

import clsx from "clsx";
import React from "react";

import { Calendar } from "@/assets/icons/ds";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import type { FormFieldProps } from "@/components/Interface/FormFields/FormField/FormField.utils.ts";

/**
 * This component uses the native HTML input type="date" under the hood. Because
 * of this, it must take a value in YYYY-MM-DD format, which will be displayed
 * it in the browser's locale (not the app's locale).
 */
export const DateInput = <TFormValues extends object>(
  props: React.PropsWithChildren<FormFieldProps<TFormValues>>,
) => {
  const icon = (
    <Calendar
      data-testid="calendar-icon"
      className={clsx(
        "cursor-pointer",
        props.options?.readOnly || props.options?.disabled ? "opacity-40" : "",
      )}
      onClick={(event) => {
        // currentTarget is always the Calendar SVG regardless of which child
        // path element received the click — unlike event.target which may be
        // a nested <path> and would break the fixed-depth DOM traversal.
        const svg = event.currentTarget as SVGSVGElement;
        const input = svg.parentElement?.parentElement?.querySelector(
          "input",
        ) as HTMLInputElement | null;
        if (input?.click) {
          input.click();
        }
        if (input && "showPicker" in input) {
          // showPicker() is not supported in all browsers.
          (input as HTMLInputElement & { showPicker(): void }).showPicker();
        }
      }}
    />
  );

  // Stacked mode: FormField owns the visible grey box — this outer wrapper
  // must NOT carry form-field-container so it doesn't trigger a second grey
  // box via the stacked CSS selector (same fix as FormFieldWithHintProps).
  if (props.layout === "stacked") {
    return (
      <FormField {...props} className={props.className} type="date" icon={icon}>
        {props.children}
      </FormField>
    );
  }

  return (
    <div className="form-field-container">
      <FormField {...props} className={props.className} type="date" icon={icon}>
        {props.children}
      </FormField>
    </div>
  );
};
